import { Component, ElementRef, Inject, Optional, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Project } from 'src/app/core/objects/project';
import { ProjectApiService } from 'src/app/core/services/project-api.service';
import { ProjectService } from 'src/app/core/services/project.service';
import {
  SaveProjectFormModel,
  SaveProjectService,
} from 'src/app/core/services/save-project.service';
import { UserApiService } from 'src/app/core/services/user-api.service';
import { ProjectInfo, SavedProjectBody } from 'src/app/core/types';
import { ModalOverlayRef } from 'src/app/modal/modal-overlay-ref';
import { MODAL_DATA } from '../../modal/modal.service';
import { CookieService } from 'ngx-cookie-service';
import { LanguageService } from '../../core/services/language.service';
import { MessageService } from '../../core/services/message.service';

@Component({
    selector: 'gl-share-project',
    templateUrl: './share-project.component.html',
    styleUrls: ['./share-project.component.scss'],
    standalone: false
})
export class ShareProjectComponent {
  public linkForm: UntypedFormGroup;
  public saveForm: SaveProjectFormModel;
  public success: boolean;
  public projectDescription: string;
  public isGeneratedLink: boolean;
  public project: Project;
  public isMobile: boolean;
  @ViewChild('linkInput', { static: false, read: ElementRef })
  private linkInput: ElementRef<HTMLInputElement>;

  constructor(
    public modalRef: ModalOverlayRef,
    private saveProjectService: SaveProjectService,
    private messageService: MessageService,
    private projectApiService: ProjectApiService,
    private userApiService: UserApiService,
    private projectService: ProjectService,
    private cookieService: CookieService,
    private router: Router,
    private route: ActivatedRoute,
    private languageService: LanguageService,
    @Optional() @Inject(MODAL_DATA) public data: SavedProjectBody,
  ) {
    this.isMobile = this.router.url.includes('mobile');
    this.projectService.project.subscribe((project) => (this.project = project));
    this.projectDescription = this.saveProjectService.generateVisibleDescription();
    this.saveForm = this.saveProjectService.generateForm();
    this.linkForm = this.generateLinkForm();
    if (this.data && this.data.name) {
      if (this.data.name) {
        this.setValueToFormControl('name', this.data.name);
      }
      if (this.data.note) {
        this.setValueToFormControl('note', this.data.note);
      }
    } else {
      this.projectApiService.getProjectInfo().subscribe((projectInfo: ProjectInfo) => {
        if (projectInfo && projectInfo.name) {
          this.setValueToFormControl('name', projectInfo.name);
        }
        if (projectInfo && projectInfo.note) {
          this.setValueToFormControl('note', projectInfo.note);
        }
      });
    }
    this.saveForm.addControl('saveOption', new UntypedFormControl(true));
    this.modalRef.backgroundClicked();

    if (this.isMobile) {
      this.isGeneratedLink = true;
      this.generateLink();
    }
  }

  public setValueToFormControl(name: string, value: string) {
    this.saveForm.controls[name].setValue(value);
  }

  public onClose() {
    this.modalRef.close();
  }

  public onSubmit() {
    if (this.saveForm.valid) {
      if (this.saveForm.value.saveOption) {
        if (this.data && this.data.id !== this.project.id) {
          this.updateProject();
        } else {
          this.saveProject();
        }
      } else {
        this.isGeneratedLink = true;
        this.generateLink();
      }
    }
  }

  public copyLink() {
    this.linkInput.nativeElement.focus();
    this.linkInput.nativeElement.select();
    document.execCommand('copy');
    this.success = true;
  }

  public generateLinkForm() {
    return new UntypedFormGroup({
      link: new UntypedFormControl(null),
    });
  }

  public saveProject() {
    const projectData = {
      description: this.saveProjectService.generateProjectDescription(),
      name: this.saveForm.value.name,
      note: this.saveForm.value.note,
    };
    this.userApiService.saveProject(projectData).subscribe(() => {
      this.isGeneratedLink = true;
      this.generateLink();
      this.messageService.sendMessage({ name: 'savedProject' });
    });
  }

  public updateProject() {
    const projectData = {
      id: this.data.id,
      name: this.saveForm.value.name,
      note: this.saveForm.value.note,
    };
    const isCompanyProject: boolean = this.data.isCompanyProject === true;
    if (isCompanyProject) {
      this.userApiService.updateCompanyProject(projectData).subscribe(() => {
        this.isGeneratedLink = true;
        this.generateLink();
        this.messageService.sendMessage({ name: 'savedProject' });
      });
    } else {
      this.userApiService.updateProject(projectData).subscribe(() => {
        this.isGeneratedLink = true;
        this.generateLink();
        this.messageService.sendMessage({ name: 'savedProject' });
      });
    }
  }

  private generateLink() {
    if (this.data && this.data.id) {
      const projectData = {
        id: this.data.id,
        name: this.saveForm.value.name,
        note: this.saveForm.value.note,
      };
      const isCompanyProject: boolean = this.data.isCompanyProject === true;
      if (isCompanyProject) {
        this.userApiService.shareCompanyProject(projectData).subscribe((id: string) => {
          this.linkForm.patchValue({ link: this.generateLinkFromShareId(id) });
        });
      } else {
        this.userApiService
          .shareSavedProject(projectData)
          .subscribe((id: string) =>
            this.linkForm.patchValue({ link: this.generateLinkFromShareId(id) }),
          );
      }
    } else {
      const data: { name: string; note: string } = {
        name: this.saveForm.value.name,
        note: this.saveForm.value.note,
      };
      this.projectApiService
        .share(data)
        .subscribe((id: string) =>
          this.linkForm.patchValue({ link: this.generateLinkFromShareId(id) }),
        );
    }
  }

  private generatePreferences(): string {
    // const isOptimization = this.cookieService.get('optimization').includes('true');
    const isOptimization = true;
    const isLoadName = this.cookieService.get('loadName').includes('true');
    const isMeasure = this.cookieService.get('measure').includes('true');
    const optimization = isOptimization ? 'opt-' : '';
    const loadName = isLoadName ? 'names-' : '';
    const measure = isMeasure ? 'measure-' : '';
    const opened = 'opened';
    const preferences = `&pref=${optimization}${loadName}${measure}${opened}`;
    return preferences;
  }

  private generateLinkFromShareId(id: string): string {
    const lang = this.languageService.getDefaultLanguage();
    let link = location.origin + '/' + lang + '/session/?id=' + id + this.generatePreferences();
    const brandingId = this.route.snapshot.queryParams['company-branding'];
    if (brandingId) {
      link = link + '&company-branding=' + brandingId;
    }
    return link;
  }
}
